// Third-party
import React from 'react';
import { withFormik, FormikProps, Form } from 'formik';
import * as Yup from 'yup';

// Namespaces
import * as ProposalNS from '../../../modules/proposal/Proposal';

// Utils
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import { FormattedMessage, formatMessage } from '../../../utils/i18n';

// Components
import { Button } from '@capintel/component-lib/button';
import { FormInput } from '../../components/forms';
import { Props as InputProps } from '../../components/inputs/Input';
import { Typography } from '@capintel/component-lib/typography';

interface UserFields {
  fname?: string;
  lname?: string;
  company?: string;
  title?: string;
  email?: string;
  phone?: string;
  date?: string;
  clientName?: string;
}
interface FormValues extends UserFields {
  defaultValues?: UserFields;
}

export enum AdvisorBrandingField {
  ClientName = 'clientName',
  FirstName = 'fname',
  LastName = 'lname',
  Company = 'company',
  Title = 'title',
  Email = 'email',
  Phone = 'phone',
  Date = 'date',
}
interface ParentProps {
  // eslint-disable-next-line react/no-unused-prop-types
  initialValues?: Partial<FormValues>;
  onSubmit: (values: ProposalNS.ProposalDownloadFormValues) => Promise<void>;
  fields?: string[];
  showSectionHeader: boolean;
  allowFormReset: boolean;
}

type Props = ParentProps & FormikProps<FormValues>;

const canSubmit = (props: Props) => {
  const { errors } = props;
  return Object.keys(errors).length === 0;
};

class InputWithGuard extends React.PureComponent<Partial<InputProps> & { fields?: string[] }> {
  render() {
    const { fields, ...inputProps } = this.props;

    if (!inputProps.name || !fields?.includes(inputProps.name)) {
      return null;
    }

    return <FormInput {...inputProps} />;
  }
}

const AdvisorBrandingForm = ({ fields = [], ...props }: Props) => {
  const header = !props.showSectionHeader ? null : (
    <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerXs">
      <FormattedMessage id="Proposal.AdvisorInfoForm.AdvisorInfo" />
    </Typography>
  );

  return (
    <Form className="advisor-branding-form">
      {fields?.some(field => {
        const advisorFields = [
          AdvisorBrandingField.FirstName,
          AdvisorBrandingField.LastName,
          AdvisorBrandingField.Company,
          AdvisorBrandingField.Title,
          AdvisorBrandingField.Email,
          AdvisorBrandingField.Phone,
        ] as string[];

        return advisorFields.includes(field);
      }) && (
        <>
          {header}
          <div className="field-group">
            <InputWithGuard fields={fields} key="fname" name="fname" label={formatMessage('Proposal.AdvisorInfoForm.FirstName')} type="text" />

            <InputWithGuard fields={fields} key="lname" name="lname" label={formatMessage('Proposal.AdvisorInfoForm.LastName')} type="text" />

            <InputWithGuard fields={fields} key="company" name="company" label={formatMessage('Proposal.AdvisorInfoForm.Company')} type="text" />

            <InputWithGuard fields={fields} key="title" name="title" label={formatMessage('Proposal.AdvisorInfoForm.Title')} type="text" />

            <InputWithGuard fields={fields} key="email" name="email" label={formatMessage('Proposal.AdvisorInfoForm.Email')} type="email" />

            <InputWithGuard
              fields={fields}
              key="phone"
              name="phone"
              label={formatMessage('Proposal.AdvisorInfoForm.Phone')}
              type="number"
              inputMask="(999) 999-9999"
            />
          </div>
        </>
      )}

      {fields?.includes(AdvisorBrandingField.ClientName) && (
        <>
          <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerXs">
            <FormattedMessage id="Proposal.AdvisorInfoForm.ClientInfo" />
          </Typography>
          <div className="field-group">
            <InputWithGuard fields={fields} key="clientName" name="clientName" label={formatMessage('Proposal.AdvisorInfoForm.ClientName')} type="text" />
          </div>
        </>
      )}

      {fields?.includes(AdvisorBrandingField.Date) && (
        <>
          <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerXs">
            <FormattedMessage id="Proposal.AdvisorInfoForm.ProposalDate" />
          </Typography>
          <div className="field-group">
            <InputWithGuard
              fields={fields}
              key="date"
              name="date"
              label={formatMessage('Proposal.AdvisorInfoForm.Date')}
              type="text"
              placeholder="YYYY-MM-DD"
              inputMask="9999-99-99"
            />
          </div>
        </>
      )}

      <div className="actions">
        {props.allowFormReset && (
          <Button
            variant="secondary"
            onClick={() => {
              props.resetForm({
                ...props.values,
                ...props.initialValues.defaultValues,
              });
            }}
          >
            <FormattedMessage id="Common.RevertToOrginal" />
          </Button>
        )}

        <Button variant="primary" type="submit" disabled={!canSubmit(props)}>
          <FormattedMessage id="Common.Save" />
        </Button>
      </div>
    </Form>
  );
};

const validationSchema = Yup.object().shape({
  fname: Yup.string(),
  lname: Yup.string(),
  company: Yup.string(),
  title: Yup.string(),
  email: Yup.string().email(),
  clientName: Yup.string(),
  phone: Yup.string(),
  date: Yup.string(),
});

const FormikWrapper = withFormik<ParentProps, FormValues>({
  validationSchema,
  mapPropsToValues: (props: ParentProps) => ({
    fname: props.initialValues?.fname || '',
    lname: props.initialValues?.lname || '',
    company: props.initialValues?.company || '',
    title: props.initialValues?.title || '',
    email: props.initialValues?.email || '',
    clientName: props.initialValues?.clientName || '',
    phone: props.initialValues?.phone || '',
    date: props.initialValues?.date || '',
    defaultValues: props.initialValues?.defaultValues,
  }),
  handleSubmit: (values, { props, setSubmitting }) => {
    const { fname, lname, company, title, email, clientName, phone, date, defaultValues } = values;

    const downloadFormValues: ProposalNS.ProposalDownloadFormValues = {
      headerInfo: { fname, lname, company, title, email, phone, date, clientName },
      defaultValues,
      overrideMock: true,
    };

    if (clientName) {
      downloadFormValues.contact = clientName;
    }

    props.onSubmit(downloadFormValues).finally(() => {
      setSubmitting(false);
    });
  },
});

export default FormikWrapper(AdvisorBrandingForm);
